import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell, AlertTriangle, Shield, Zap, Info, CheckCircle,
  ChevronDown, ChevronRight, Globe, Plus, Trash2, Send,
  RefreshCw, X, Check
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function safeFetch(url, opts = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(opts.method || 'GET', url);
    if (opts.headers) Object.entries(opts.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => { try { resolve(JSON.parse(xhr.responseText)); } catch { resolve({ error: xhr.responseText }); } };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(opts.body || null);
  });
}

const SEVERITY_CONFIG = {
  info: { icon: Info, color: 'blue', bg: 'blue-500/[0.05]', border: 'blue-500/20' },
  warning: { icon: AlertTriangle, color: 'amber', bg: 'amber-500/[0.05]', border: 'amber-500/20' },
  critical: { icon: Shield, color: 'red', bg: 'red-500/[0.05]', border: 'red-500/20' },
  emergency: { icon: Zap, color: 'red', bg: 'red-500/[0.08]', border: 'red-500/30' },
};

function AlertCard({ alert, onRead }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
  const Icon = cfg.icon;

  return (
    <div
      className={`border rounded-xl overflow-hidden transition-all ${alert.read ? 'opacity-60' : ''} bg-${cfg.bg} border-${cfg.border}`}
      data-testid={`alert-${alert.alert_id}`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
      >
        <Icon className={`w-4 h-4 text-${cfg.color}-400 shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium bg-${cfg.color}-500/15 text-${cfg.color}-400 uppercase`}>
              {alert.severity}
            </span>
            <span className="text-sm text-white font-medium truncate">{alert.title}</span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5 truncate">{alert.message}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-gray-600">{new Date(alert.timestamp).toLocaleString()}</span>
          {!alert.read && (
            <button
              onClick={e => { e.stopPropagation(); onRead(alert.alert_id); }}
              className="p-1 hover:bg-white/5 rounded transition-colors"
              title="Mark as read"
            >
              <Check className="w-3 h-3 text-gray-500" />
            </button>
          )}
          {expanded ? <ChevronDown className="w-4 h-4 text-gray-600" /> : <ChevronRight className="w-4 h-4 text-gray-600" />}
        </div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 py-3 border-t border-white/5 space-y-2">
              <p className="text-sm text-gray-300">{alert.message}</p>
              {alert.details && Object.keys(alert.details).length > 0 && (
                <pre className="text-xs text-gray-500 bg-black/20 rounded-lg p-3 overflow-x-auto">
                  {JSON.stringify(alert.details, null, 2)}
                </pre>
              )}
              <div className="flex gap-4 text-xs text-gray-600">
                <span>Escrow: <span className="text-gray-400 font-mono">{alert.escrow_id?.slice(0, 12)}...</span></span>
                <span>Event: <span className="text-gray-400">{alert.event_type}</span></span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function WebhookCard({ webhook, onDelete, onTest }) {
  const successRate = webhook.total_deliveries > 0
    ? ((webhook.successful_deliveries / webhook.total_deliveries) * 100).toFixed(0)
    : '—';

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4 flex items-center justify-between" data-testid={`webhook-${webhook.webhook_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <div className={`w-2 h-2 rounded-full ${webhook.active ? 'bg-emerald-400' : 'bg-gray-600'}`} />
          <span className="text-sm text-white font-mono truncate">{webhook.url}</span>
        </div>
        <div className="flex gap-3 text-xs text-gray-600">
          <span>Events: <span className="text-gray-400">{webhook.events?.join(', ')}</span></span>
          <span>Delivered: <span className="text-gray-400">{webhook.total_deliveries}</span></span>
          <span>Success: <span className={`${successRate === '—' ? 'text-gray-500' : parseInt(successRate) > 90 ? 'text-emerald-400' : 'text-red-400'}`}>{successRate}%</span></span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => onTest(webhook.webhook_id)} className="p-2 hover:bg-white/5 rounded-lg transition-colors text-gray-500 hover:text-emerald-400" title="Test">
          <Send className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onDelete(webhook.webhook_id)} className="p-2 hover:bg-white/5 rounded-lg transition-colors text-gray-500 hover:text-red-400" title="Delete">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function AlertDashboard() {
  const [agentAddress, setAgentAddress] = useState('');
  const [lookupAddr, setLookupAddr] = useState('');
  const [alerts, setAlerts] = useState(null);
  const [webhooks, setWebhooks] = useState(null);
  const [stats, setStats] = useState(null);
  const [showAddWebhook, setShowAddWebhook] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [newSecret, setNewSecret] = useState('');
  const [newEvents, setNewEvents] = useState(['warning', 'critical', 'emergency']);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    safeFetch(`${API}/api/agent-credit/alerts/stats`).then(d => { if (!d.detail) setStats(d); });
  }, []);

  const lookup = async () => {
    if (!agentAddress) return;
    setLookupAddr(agentAddress);
    const [alertData, whData] = await Promise.all([
      safeFetch(`${API}/api/agent-credit/alerts/agent/${agentAddress}`),
      safeFetch(`${API}/api/agent-credit/alerts/webhooks/agent/${agentAddress}`),
    ]);
    if (!alertData.detail) setAlerts(alertData);
    if (!whData.detail) setWebhooks(whData);
  };

  const markRead = async (alertId) => {
    await safeFetch(`${API}/api/agent-credit/alerts/${alertId}/read`, { method: 'PUT' });
    setAlerts(prev => prev ? {
      ...prev,
      unread: prev.unread - 1,
      alerts: prev.alerts.map(a => a.alert_id === alertId ? { ...a, read: true } : a)
    } : prev);
  };

  const markAllRead = async () => {
    await safeFetch(`${API}/api/agent-credit/alerts/agent/${lookupAddr}/read-all`, { method: 'PUT' });
    setAlerts(prev => prev ? { ...prev, unread: 0, alerts: prev.alerts.map(a => ({ ...a, read: true })) } : prev);
  };

  const addWebhook = async () => {
    if (!newUrl) return;
    const res = await safeFetch(`${API}/api/agent-credit/alerts/webhooks/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agent_address: lookupAddr, url: newUrl, secret: newSecret || null, events: newEvents }),
    });
    if (!res.detail && !res.error) {
      setShowAddWebhook(false);
      setNewUrl('');
      setNewSecret('');
      lookup();
    }
  };

  const deleteWebhook = async (id) => {
    await safeFetch(`${API}/api/agent-credit/alerts/webhooks/${id}`, { method: 'DELETE' });
    lookup();
  };

  const testWebhook = async (id) => {
    setTestResult(null);
    const res = await safeFetch(`${API}/api/agent-credit/alerts/webhooks/${id}/test`, { method: 'POST' });
    setTestResult(res);
    setTimeout(() => setTestResult(null), 5000);
  };

  const toggleEvent = (evt) => {
    setNewEvents(prev => prev.includes(evt) ? prev.filter(e => e !== evt) : [...prev, evt]);
  };

  return (
    <div data-testid="alert-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-400" />
            Liquidation Alerts
          </h2>
          <p className="text-sm text-gray-500 mt-1">Proactive notifications with webhook delivery for agent frameworks</p>
        </div>
      </div>

      {/* Global Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
            <div className="text-xs text-gray-500 mb-1">Total Alerts</div>
            <div className="text-lg font-bold text-white">{stats.total_alerts}</div>
            <div className="text-xs text-amber-400">{stats.unread_alerts} unread</div>
          </div>
          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
            <div className="text-xs text-gray-500 mb-1">By Severity</div>
            <div className="flex gap-2 mt-1">
              {Object.entries(stats.by_severity || {}).map(([sev, count]) => (
                <span key={sev} className={`text-xs px-1.5 py-0.5 rounded bg-${SEVERITY_CONFIG[sev]?.color || 'gray'}-500/15 text-${SEVERITY_CONFIG[sev]?.color || 'gray'}-400`}>
                  {sev}: {count}
                </span>
              ))}
            </div>
          </div>
          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
            <div className="text-xs text-gray-500 mb-1">Active Webhooks</div>
            <div className="text-lg font-bold text-white">{stats.active_webhooks}</div>
          </div>
          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
            <div className="text-xs text-gray-500 mb-1">Webhook Deliveries</div>
            <div className="text-lg font-bold text-white">{stats.webhook_deliveries?.total || 0}</div>
            <div className="text-xs text-emerald-400">{stats.webhook_deliveries?.successful || 0} successful</div>
          </div>
        </div>
      )}

      {/* Agent Lookup */}
      <div className="flex gap-3 mb-6">
        <input
          value={agentAddress}
          onChange={e => setAgentAddress(e.target.value)}
          placeholder="Enter agent address (0x...) to view alerts and webhooks"
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm font-mono focus:outline-none focus:border-amber-500/50 placeholder:text-gray-700"
          data-testid="alert-agent-input"
        />
        <button onClick={lookup} disabled={!agentAddress} className="px-5 py-3 bg-amber-500/20 border border-amber-500/30 rounded-lg text-sm font-medium text-amber-400 hover:bg-amber-500/30 transition-all disabled:opacity-50" data-testid="alert-lookup-btn">
          Lookup
        </button>
      </div>

      {lookupAddr && (
        <div className="grid grid-cols-12 gap-6">
          {/* Left: Alert Feed */}
          <div className="col-span-8">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Bell className="w-4 h-4 text-amber-400" />
                Alert Feed
                {alerts?.unread > 0 && (
                  <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">{alerts.unread}</span>
                )}
              </h3>
              {alerts?.unread > 0 && (
                <button onClick={markAllRead} className="text-xs text-gray-500 hover:text-white transition-colors" data-testid="mark-all-read">
                  Mark all read
                </button>
              )}
            </div>

            {alerts?.alerts?.length > 0 ? (
              <div className="space-y-2">
                {alerts.alerts.map(alert => (
                  <AlertCard key={alert.alert_id} alert={alert} onRead={markRead} />
                ))}
              </div>
            ) : (
              <div className="text-center py-10 bg-white/[0.02] border border-white/5 border-dashed rounded-xl">
                <CheckCircle className="w-8 h-8 text-emerald-500/30 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No alerts for this agent</p>
                <p className="text-xs text-gray-700 mt-1">Alerts are generated by the hourly auto-liquidation cron</p>
              </div>
            )}
          </div>

          {/* Right: Webhooks */}
          <div className="col-span-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Globe className="w-4 h-4 text-blue-400" />
                Webhooks
              </h3>
              <button
                onClick={() => setShowAddWebhook(!showAddWebhook)}
                className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                data-testid="add-webhook-btn"
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>

            {/* Add Webhook Form */}
            <AnimatePresence>
              {showAddWebhook && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden mb-3"
                >
                  <div className="bg-white/[0.02] border border-blue-500/20 rounded-xl p-4 space-y-3">
                    <input
                      value={newUrl}
                      onChange={e => setNewUrl(e.target.value)}
                      placeholder="https://your-agent.com/webhook"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-blue-500/50 placeholder:text-gray-700"
                      data-testid="webhook-url-input"
                    />
                    <input
                      value={newSecret}
                      onChange={e => setNewSecret(e.target.value)}
                      placeholder="Signing secret (optional)"
                      type="password"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-blue-500/50 placeholder:text-gray-700"
                    />
                    <div className="flex gap-2">
                      {['warning', 'critical', 'emergency'].map(evt => (
                        <button
                          key={evt}
                          onClick={() => toggleEvent(evt)}
                          className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${
                            newEvents.includes(evt)
                              ? `bg-${SEVERITY_CONFIG[evt].color}-500/15 text-${SEVERITY_CONFIG[evt].color}-400 border border-${SEVERITY_CONFIG[evt].color}-500/20`
                              : 'bg-white/5 text-gray-600 border border-white/10'
                          }`}
                        >
                          {evt}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={addWebhook} className="flex-1 py-2 bg-blue-500/20 border border-blue-500/30 rounded-lg text-sm text-blue-400 hover:bg-blue-500/30 transition-all" data-testid="webhook-save-btn">
                        Register
                      </button>
                      <button onClick={() => setShowAddWebhook(false)} className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-gray-400">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Test Result */}
            {testResult && (
              <div className={`text-xs px-3 py-2 rounded-lg mb-3 ${testResult.success ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
                {testResult.message} {testResult.status_code && `(${testResult.status_code})`}
              </div>
            )}

            {webhooks?.webhooks?.length > 0 ? (
              <div className="space-y-2">
                {webhooks.webhooks.map(wh => (
                  <WebhookCard key={wh.webhook_id} webhook={wh} onDelete={deleteWebhook} onTest={testWebhook} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-white/[0.02] border border-white/5 border-dashed rounded-xl">
                <Globe className="w-6 h-6 text-gray-700 mx-auto mb-2" />
                <p className="text-xs text-gray-600">No webhooks registered</p>
                <p className="text-xs text-gray-700 mt-1">Add a URL to receive real-time alerts</p>
              </div>
            )}

            {/* SDK Integration Hint */}
            <div className="mt-4 bg-white/[0.02] border border-white/5 rounded-xl p-4">
              <p className="text-xs text-gray-500 leading-relaxed">
                <span className="text-blue-400 font-medium">SDK Integration:</span> Agent frameworks can register webhooks via the API and receive signed payloads with HMAC verification. Alerts fire on deadline warnings, credit freezes, grace periods, and liquidations.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!lookupAddr && (
        <div className="text-center py-12 bg-white/[0.02] border border-white/5 border-dashed rounded-xl">
          <Bell className="w-10 h-10 text-gray-700 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Enter an agent address to view alerts and manage webhooks</p>
          <p className="text-xs text-gray-700 mt-1">Proactive alerts fire before liquidation — giving agents time to act</p>
        </div>
      )}
    </div>
  );
}
